import { useState, useEffect } from "react";
import { useNavigate, useLocation, Link } from "react-router-dom";
import { api, apiErr } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { toast } from "sonner";
import { HeartPulse, ArrowLeft } from "lucide-react";
import { isNativeApp, nativeGoogleSignIn } from "../lib/nativeAuth";
import { setTokens } from "../lib/authToken";
import { PasswordInput } from "../components/PasswordInput";
import { LanguageSwitcher } from "../components/LanguageSwitcher";

export default function Login() {
  const navigate = useNavigate();
  const location = useLocation();
  const { setUser } = useAuth();
  const [mode, setMode] = useState("login");
  const [form, setForm] = useState({ name: "", first_name: "", surname: "", email: "", password: "", password2: "", username: "", code: "", phone: "" });
  const [confirmInfo, setConfirmInfo] = useState(null);
  const [usernameHint, setUsernameHint] = useState(null);
  const [busy, setBusy] = useState(false);
  const [remember, setRemember] = useState(true);

  useEffect(() => {
    if (location.state?.notice) toast.info(location.state.notice);
  }, [location.state]);

  useEffect(() => {
    const m = new URLSearchParams(location.search).get("mode");
    if (["login", "register", "register-public"].includes(m)) setMode(m);
  }, [location.search]);

  const upd = (k) => (e) => setForm({ ...form, [k]: e.target.value });
  const fullName = `${(form.first_name || "").trim()} ${(form.surname || "").trim()}`.trim();

  // Suggest a username from the full name (surname + first initial) when the field is empty.
  useEffect(() => {
    if (mode !== "register-public" || !fullName || form.username) return;
    let active = true;
    const t = setTimeout(async () => {
      try {
        const { data } = await api.get("/auth/username-suggestion", { params: { name: fullName } });
        if (active && data.suggestion) { setForm((f) => (f.username ? f : { ...f, username: data.suggestion })); }
      } catch { /* ignore */ }
    }, 500);
    return () => { active = false; clearTimeout(t); };
  }, [fullName, mode]); // eslint-disable-line react-hooks/exhaustive-deps

  // Live availability check for the chosen username.
  useEffect(() => {
    if (mode !== "register-public" || !form.username) { setUsernameHint(null); return; }
    let active = true;
    const t = setTimeout(async () => {
      try {
        const { data } = await api.get("/auth/username-suggestion", { params: { username: form.username } });
        if (active) setUsernameHint(data);
      } catch { /* ignore */ }
    }, 400);
    return () => { active = false; clearTimeout(t); };
  }, [form.username, mode]);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      if (mode === "forgot") {
        const { data } = await api.post("/auth/forgot-password", { identifier: form.email });
        toast.success(data.message || "If an account matches, a reset code was sent.");
        setMode("reset");
        return;
      }
      if (mode === "reset") {
        const { data } = await api.post("/auth/reset-password", {
          identifier: form.email, code: form.code, new_password: form.password,
        });
        toast.success(data.message || "Password updated. Please sign in.");
        setMode("login");
        setForm({ ...form, code: "", password: "" });
        return;
      }
      if (mode === "forgot-username") {
        const { data } = await api.post("/auth/forgot-username", { identifier: form.email });
        toast.success(data.message || "If an account matches, your username was sent.");
        setMode("login");
        setForm({ ...form, email: "" });
        return;
      }
      if (mode === "register-public") {
        if (form.password.length < 5) { toast.error("Password must be at least 5 characters."); setBusy(false); return; }
        if (form.password !== form.password2) { toast.error("Passwords do not match."); setBusy(false); return; }
        const { data } = await api.post("/auth/register-public", {
          first_name: form.first_name, surname: form.surname, name: fullName, username: form.username, password: form.password,
        });
        // #12: show a confirmation, THEN auto-sign-in (no extra login step).
        setConfirmInfo({ name: fullName, username: data.username });
        setTimeout(() => {
          setTokens({ access_token: data.access_token, refresh_token: data.refresh_token, remember: true });
          try { sessionStorage.setItem("rc_welcome", "1"); } catch { /* storage off */ }
          setUser(data.user);
          navigate("/portal", { replace: true });
        }, 2400);
        return;
      }
      if (mode === "register") {
        const { data } = await api.post("/auth/register", form);
        toast.success(data.message || "Registration submitted for approval");
        setMode("login");
        setForm({ name: "", email: form.email, password: "" });
        return;
      }
      const { data } = await api.post("/auth/login", { identifier: form.email, password: form.password, remember });
      setTokens({ access_token: data.access_token, refresh_token: data.refresh_token, remember });
      setUser(data.user);
      toast.success("Welcome back");
      const redirectTo = new URLSearchParams(location.search).get("redirect");
      if (redirectTo && data.user.role === "public") navigate(redirectTo, { replace: true });
      else navigate(data.user.role === "public" ? "/portal" : "/admin", { replace: true });
    } catch (err) {
      toast.error(apiErr(err));
    } finally {
      setBusy(false);
    }
  };

  const googleLogin = async (intent) => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    if (isNativeApp()) {
      // Native Capacitor apps: use the native Google Sign-In plugin (idToken -> backend).
      setBusy(true);
      try {
        const idToken = await nativeGoogleSignIn();
        const { data } = await api.post("/auth/google-native", {
          id_token: idToken,
          intent: intent === "patient" ? "patient" : undefined,
        });
        if (data.pending) {
          toast.info(data.message);
          return;
        }
        setTokens({ access_token: data.access_token, refresh_token: data.refresh_token, remember: true });
        try { if (data.user.role === "public") sessionStorage.setItem("rc_welcome", "1"); } catch { /* storage off */ }
        setUser(data.user);
        toast.success("Welcome");
        navigate(data.user.role === "public" ? "/portal" : "/admin", { replace: true });
      } catch (err) {
        toast.error(apiErr(err) || "Google sign-in failed");
      } finally {
        setBusy(false);
      }
      return;
    }
    // Web: Emergent-managed Google Auth via browser redirect.
    const target = intent === "patient" ? "/portal?g=patient" : "/admin";
    const redirectUrl = window.location.origin + target;
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  return (
    <div className="min-h-screen grid md:grid-cols-2">
      <div className="fixed top-3 right-3 z-50"><LanguageSwitcher /></div>
      {confirmInfo && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/60 p-4" data-testid="registration-success-modal">
          <div className="bg-white rounded-xl p-8 max-w-md text-center">
            <div className="w-14 h-14 rounded-full bg-calm/15 flex items-center justify-center mx-auto mb-4">
              <HeartPulse className="w-7 h-7 text-calm" />
            </div>
            <h2 className="font-heading font-bold text-2xl text-ink">Registration Successful</h2>
            <p className="text-muted2 mt-3 text-sm">Your account has been created successfully.</p>
            <div className="bg-line/40 rounded-lg p-4 mt-4 text-left text-sm">
              <p><span className="text-muted2">Name:</span> <b className="text-ink">{confirmInfo.name}</b></p>
              <p className="mt-1"><span className="text-muted2">Username:</span> <b className="text-ink" data-testid="confirm-username">{confirmInfo.username}</b></p>
            </div>
            <p className="text-sm text-clinical font-semibold mt-4 inline-flex items-center gap-2">
              <span className="w-4 h-4 border-2 border-clinical/30 border-t-clinical rounded-full animate-spin" />
              You are now being signed in and taken to your account…
            </p>
          </div>
        </div>
      )}
      <div
        className="hidden md:flex flex-col justify-between p-12 bg-clinical text-white relative"
        style={{
          backgroundImage:
            "linear-gradient(rgba(21,56,89,0.82),rgba(21,56,89,0.92)), url(https://images.unsplash.com/photo-1580281657529-557a6abb6387?w=1200)",
          backgroundSize: "cover",
          backgroundPosition: "center",
        }}
      >
        <Link to="/" className="flex flex-col gap-0.5" data-testid="login-home-link">
          <span className="flex items-center gap-2 font-heading font-bold text-lg"><HeartPulse className="w-6 h-6 text-alert" /> Hutanohwevanhu.org</span>
          <span className="text-[11px] font-semibold text-white/70 tracking-tight pl-8">Rural Health Promotion &amp; Telemedicine</span>
        </Link>
        <div>
          <h1 className="font-heading font-black text-4xl lg:text-5xl tracking-tighter leading-[1.05]">
            Staff portal for community health communication.
          </h1>
          <p className="mt-6 text-white/80 text-base leading-relaxed max-w-md">
            Publish announcements, track antivenom & blood stock, manage clinic
            schedules and reach your community — in real time.
          </p>
        </div>
        <p className="text-xs uppercase tracking-[0.2em] text-white/60">Remote & rural health network</p>
      </div>

      <div className="flex items-center justify-center p-6 sm:p-12 bg-sand">
        <div className="w-full max-w-sm">
          <Link to="/" className="md:hidden inline-flex items-center gap-1 text-sm text-muted2 mb-8" data-testid="login-back-link">
            <ArrowLeft className="w-4 h-4" /> Back to feed
          </Link>
          <h2 className="font-heading font-bold text-2xl sm:text-3xl tracking-tight text-ink">
            {mode === "login" ? "Sign in"
              : mode === "forgot" ? "Reset your password"
              : mode === "reset" ? "Enter reset code"
              : mode === "forgot-username" ? "Recover your username"
              : mode === "register-public" ? "Create patient account" : "Create staff account"}
          </h2>
          <p className="text-muted2 mt-2 text-sm">
            {mode === "forgot" ? "Enter your email or WhatsApp number and we'll send a reset code."
              : mode === "reset" ? "Enter the 6-digit code we sent and choose a new password."
              : mode === "forgot-username" ? "Enter your registered email or WhatsApp number and we'll send you your username."
              : mode === "register-public" ? "For community members booking virtual consultations."
              : "For clinical personnel and health promotion nurses."}
          </p>
          {(mode === "register" || mode === "register-public") && (
            <p className="mt-3 text-xs text-clinical bg-clinical/5 border border-clinical/20 rounded-md px-3 py-2">
              {mode === "register-public"
                ? "Your patient account is ready instantly — you'll get a unique appointment code as soon as you sign up."
                : "New staff accounts require administrator approval before the first sign-in."}
            </p>
          )}

          <button
            onClick={() => googleLogin(mode === "register-public" ? "patient" : "staff")}
            data-testid="google-login-btn"
            className={`mt-8 w-full flex items-center justify-center gap-3 border border-line bg-white rounded-md py-3 font-medium text-ink hover:bg-line/40 transition-colors ${mode === "forgot" || mode === "reset" || mode === "forgot-username" ? "hidden" : ""}`}
          >
            <img src="https://www.google.com/favicon.ico" alt="" className="w-4 h-4" />
            {mode === "register-public" ? "Continue with Google as patient" : "Continue with Google"}
          </button>

          <div className={`flex items-center gap-4 my-6 ${mode === "forgot" || mode === "reset" || mode === "forgot-username" ? "hidden" : ""}`}>
            <div className="h-px bg-line flex-1" />
            <span className="text-xs uppercase tracking-[0.2em] text-muted2">or</span>
            <div className="h-px bg-line flex-1" />
          </div>

          <form onSubmit={submit} className="space-y-4">
            {(mode === "register" || mode === "register-public") && (
              <div className="grid grid-cols-2 gap-3">
                <input
                  data-testid="first-name-input"
                  required
                  placeholder="First name"
                  value={form.first_name}
                  onChange={upd("first_name")}
                  className="w-full border border-line bg-white rounded-md px-4 py-3 text-sm outline-none focus:border-clinical"
                />
                <input
                  data-testid="surname-input"
                  placeholder="Surname"
                  value={form.surname}
                  onChange={upd("surname")}
                  className="w-full border border-line bg-white rounded-md px-4 py-3 text-sm outline-none focus:border-clinical"
                />
              </div>
            )}
            {mode === "register-public" && (
              <div>
                <input
                  data-testid="username-input"
                  required
                  placeholder="Choose a username"
                  value={form.username}
                  onChange={(e) => setForm((f) => ({ ...f, username: e.target.value.toLowerCase().replace(/[^a-z0-9]/g, "") }))}
                  className="w-full border border-line bg-white rounded-md px-4 py-3 text-sm outline-none focus:border-clinical"
                />
                {usernameHint && form.username && (
                  usernameHint.available
                    ? <p data-testid="username-available" className="text-xs text-calm mt-1">✓ “{form.username}” is available</p>
                    : <p data-testid="username-taken" className="text-xs text-critical mt-1">
                        “{form.username}” is taken.{" "}
                        <button type="button" className="underline font-semibold" onClick={() => setForm((f) => ({ ...f, username: usernameHint.suggestion }))}>Use {usernameHint.suggestion}</button>
                      </p>
                )}
              </div>
            )}
            {mode !== "register-public" && (
              <input
                data-testid="email-input"
                required
                type={mode === "register" ? "email" : "text"}
                placeholder={
                  mode === "login" ? "Username, email or appointment code"
                  : mode === "forgot" || mode === "reset" || mode === "forgot-username" ? "Email or WhatsApp number"
                  : "Email"
                }
                value={form.email}
                onChange={upd("email")}
                className="w-full border border-line bg-white rounded-md px-4 py-3 text-sm outline-none focus:border-clinical"
              />
            )}
            {mode === "reset" && (
              <input
                data-testid="reset-code-input"
                required
                inputMode="numeric"
                placeholder="6-digit reset code"
                value={form.code}
                onChange={upd("code")}
                className="w-full border border-line bg-white rounded-md px-4 py-3 text-sm outline-none focus:border-clinical tracking-widest"
              />
            )}
            {mode !== "forgot" && mode !== "forgot-username" && (
              <PasswordInput
                testid="password-input"
                required
                placeholder={mode === "reset" ? "New password" : mode === "register-public" ? "Password (min 5 characters)" : "Password"}
                value={form.password}
                onChange={upd("password")}
                className="w-full border border-line bg-white rounded-md px-4 py-3 text-sm outline-none focus:border-clinical"
              />
            )}
            {mode === "register-public" && (
              <PasswordInput
                testid="confirm-password-input"
                required
                placeholder="Confirm password"
                value={form.password2}
                onChange={upd("password2")}
                className="w-full border border-line bg-white rounded-md px-4 py-3 text-sm outline-none focus:border-clinical"
              />
            )}
            {mode === "login" && (
              <label className="flex items-center gap-2 text-sm text-muted2 cursor-pointer select-none" data-testid="remember-me-label">
                <input
                  type="checkbox"
                  data-testid="remember-me-checkbox"
                  checked={remember}
                  onChange={(e) => setRemember(e.target.checked)}
                  className="w-4 h-4 rounded border-line accent-clinical cursor-pointer"
                />
                Keep me signed in
              </label>
            )}
            <button
              type="submit"
              disabled={busy}
              data-testid="submit-auth-btn"
              className="w-full bg-clinical text-white rounded-md py-3 font-semibold hover:bg-clinicaldark transition-colors disabled:opacity-60"
            >
              {busy ? "Please wait…"
                : mode === "login" ? "Sign in"
                : mode === "forgot" ? "Send reset code"
                : mode === "reset" ? "Set new password"
                : mode === "forgot-username" ? "Send my username"
                : "Create account"}
            </button>
          </form>

          <div className="mt-6 flex flex-col gap-2">
            {mode === "login" && (
              <>
                <button
                  onClick={() => { setMode("forgot"); setForm({ ...form, password: "", code: "" }); }}
                  data-testid="forgot-password-link"
                  className="text-sm text-muted2 hover:text-clinical hover:underline self-start"
                >
                  Forgot your password?
                </button>
                <button
                  onClick={() => { setMode("forgot-username"); setForm({ ...form, password: "", code: "" }); }}
                  data-testid="forgot-username-link"
                  className="text-sm text-muted2 hover:text-clinical hover:underline self-start"
                >
                  Forgot your username?
                </button>
              </>
            )}
            {(mode === "forgot" || mode === "reset" || mode === "forgot-username") ? (
              <button
                onClick={() => setMode("login")}
                data-testid="back-to-login-link"
                className="text-sm text-clinical hover:underline self-start"
              >
                ← Back to sign in
              </button>
            ) : (
              <>
                <button
                  onClick={() => setMode(mode === "login" ? "register" : "login")}
                  data-testid="toggle-mode-btn"
                  className="text-sm text-clinical hover:underline self-start"
                >
                  {mode === "login" ? "Staff: need an account? Register" : "Already have an account? Sign in"}
                </button>
                <button
                  onClick={() => setMode("register-public")}
                  data-testid="public-register-link"
                  className="text-sm text-calm hover:underline self-start"
                >
                  Community member? Create a patient account
                </button>
              </>
            )}
          </div>
          <p className="mt-10 text-[11px] text-muted2 text-center leading-relaxed" data-testid="login-copyright">
            © 2026 <a href="https://www.linkedin.com/in/tapiwa-mapunde-b72655423" target="_blank" rel="noopener noreferrer" data-testid="author-linkedin-link" className="font-bold text-clinical hover:underline">Tapiwa Marvin Mapunde</a>. All Rights Reserved.<br />
            Authored and designed by Tapiwa Marvin Mapunde - Master of Science in Global Healthcare Leadership.
          </p>
        </div>
      </div>
    </div>
  );
}
